import { Injectable, isDevMode } from '@angular/core';
import { MetadataEntryType } from '../constants/metadata-entry-type';
import { QuestionMetadata } from '../constants/question-metadata';

@Injectable()
export class TimeTrackingService {
  private metadataEntries: { reference: string; type: MetadataEntryType; timestamp: number }[] = [];
  private isTimeTrackingClosed = false;

  public trackMetadataEntry(ref: string, type: MetadataEntryType) {
    if (this.isTimeTrackingClosed) {
      if (isDevMode()) {
        console.log(`Question load triggered after responses submission: ${ref}`);
      }

      return;
    }

    this.metadataEntries.push({ reference: ref, type, timestamp: new Date().getTime() });
  }

  public trackUnloadEntry() {
    if (this.isTimeTrackingClosed) {
      return;
    }

    const last = this.metadataEntries[this.metadataEntries.length - 1];
    if (!!last) {
      if (last.type === MetadataEntryType.QuestionLoad) {
        this.trackMetadataEntry(last.reference, MetadataEntryType.QuestionUnload);
      } else if (last.type === MetadataEntryType.StimulusLoad) {
        this.trackMetadataEntry(last.reference, MetadataEntryType.StimulusUnload);
      }
    }
  }

  public logMetadataCsv() {
    if (isDevMode()) {
      let message = 'Item reference,Entry type,Time\n';
      this.metadataEntries.forEach((r) => {
        message = message + `${r.reference},${r.type},${r.timestamp.toString()}\n`;
      });

      console.log(message);
    }
  }

  public clearMetadataEntries() {
    this.metadataEntries = [];
    this.isTimeTrackingClosed = false;
  }

  public getMetaEntry(type: MetadataEntryType, ref: string): number | undefined {
    return this.metadataEntries.filter((e) => e.type === type).filter((e) => e.reference === ref)[0]?.timestamp;
  }

  public calculateAssessmentTime() {
    const timeDict = this.metadataEntries
      .filter((e) => e.type === MetadataEntryType.QuestionLoad || e.type === MetadataEntryType.QuestionUnload)
      .reduce((dict: { [id: string]: QuestionMetadata }, current) => {
        if (!dict[current.reference]) {
          dict[current.reference] = new QuestionMetadata();
        }

        switch (current.type) {
          case MetadataEntryType.QuestionLoad:
            dict[current.reference].loadTimestamp = current.timestamp;
            break;
          case MetadataEntryType.QuestionUnload:
            dict[current.reference].unloadTimestamp = current.timestamp;
            break;
        }

        return dict;
      }, {});

    const time = Object.keys(timeDict).reduce((sum, current) => {
      const hasLoad = !!timeDict[current].loadTimestamp;
      const hasUnload = !!timeDict[current].unloadTimestamp;
      if (hasLoad && hasUnload) {
        sum = sum + (timeDict[current].unloadTimestamp - timeDict[current].loadTimestamp);
      }

      return sum;
    }, 0);

    return time;
  }

  public closeTimeTracking() {
    this.trackUnloadEntry();
    this.isTimeTrackingClosed = true;
  }
}
